import React, {Component} from "react"
import {Row} from "react-bootstrap"
import connect from "@irati/store/connect"
import ReduxState from "@irati/store/types/ReduxState"
import linkedCardService from "@irati/perksgo/services/linkedcards"
import authentication from "@irati/auth"
import {hangPromise} from "@irati/utils/promise"
import Loading from "@irati/ui/src/Loading"
import id from "@irati/utils/id"
import {FormattedMessage} from "@irati/utils/lang"
import {DESKTOP, TABLET, PHONE_LANDSCAPE} from "@irati/ui/src/global/media"
import Slider from "@irati/ui/src/Slider"
import padlockIcon from "@irati/ui/images/padlock-icon.svg"
import LANE_TYPES from "@irati/deals/constants/laneTypes"

import {Deal as DealType, DealsTag} from "@irati/types"
import DEAL_MODES from "../../constants/dealMode"
import DEAL_PAGES from "../../constants/dealPage"
import DefaultSectionHeader from "../SectionHeader"
import DealCard from "../Deal"
import DealContainer from "./styled/DealContainer"
import DealOverlay, {BodyWithShadow} from "./styled/DealOverlay"

export const ITEM_SIZE_REGULAR = "ITEM_SIZE_REGULAR"
export const ITEM_SIZE_SMALL = "ITEM_SIZE_SMALL"

const CLASS_NAME = "feed-slider"

export const SETTINGS_REGULAR = {
  centerPadding: "30px",
  responsive: [
    {breakpoint: TABLET, settings: {slidesToShow: 1, slidesToScroll: 1}},
    {breakpoint: DESKTOP, settings: {slidesToShow: 2, slidesToScroll: 2}}
  ]
}

const SETTINGS_SMALL = {
  slidesToShow: 4,
  slidesToScroll: 4,
  responsive: [
    {breakpoint: PHONE_LANDSCAPE, settings: {slidesToShow: 1.5, slidesToScroll: 1.5}},
    {breakpoint: TABLET, settings: {slidesToShow: 2, slidesToScroll: 2}},
    {breakpoint: DESKTOP, settings: {slidesToShow: 3, slidesToScroll: 3}}
  ]
}

interface Props {
  data: DealType[],
  tag: DealsTag,
  id: string,
  className: string,
  logo: string,
  userLocation: string,
  itemSize: ITEM_SIZE_REGULAR | ITEM_SIZE_SMALL,
  isMobile: boolean,
  showDealLocation: boolean,
  capitaliseHeader: boolean,
  SectionHeader: JSX.Element,
  sectionHeaderText: string | JSX.Element,
  keyExtractor: (item: {uuid: string}, key?: string | number) => string,
  showSectionHeader: boolean,
  linkedEntityUrl: string
}

interface State {
  load: Promise<any>,
  hasLinkedCards?: boolean
}

class FeedSliderGraph extends Component<Props, State> {
  static defaultProps = {
    data: [],
    className: CLASS_NAME,
    itemSize: ITEM_SIZE_REGULAR,
    isMobile: false,
    capitaliseHeader: false,
    SectionHeader: DefaultSectionHeader,
    keyExtractor: (item: {uuid: string}) => item.uuid,
    showSectionHeader: true
  }

  state = {
    hasLinkedCards: undefined,
    load: hangPromise()
  }

  componentDidMount = () => {
    this.setState({load: this.hasLinkedCards()})
  }

  hasLinkedCards = () => {
    const userId = authentication.user().uuid
    return linkedCardService.getPerksGOCards({userId}).then((linkedCards) => {
      this.setState({hasLinkedCards: linkedCards.length > 0})
    })
  }

  get id(): string {
    const {className} = this.props
    return id(className, this.props)
  }

  get isLocked(): boolean {
    const {tag} = this.props
    const {hasLinkedCards} = this.state
    return tag && tag.name.includes(LANE_TYPES.EARN.toLowerCase()) && !hasLinkedCards
  }

  get settings(): object {
    const {data, isMobile, itemSize} = this.props
    const moreThanOne = data.length > 1
    return {
      ...SETTINGS_REGULAR,
      ...(itemSize === ITEM_SIZE_SMALL ? SETTINGS_SMALL : {}),
      centerMode: moreThanOne && isMobile,
      infinite: moreThanOne && isMobile,
      draggable: moreThanOne && isMobile
    }
  }

  get mode(): string {
    const {itemSize} = this.props
    return itemSize === ITEM_SIZE_SMALL ? DEAL_MODES.COMPACT : DEAL_MODES.DEFAULT
  }

  render() {
    const {
      data,
      tag,
      className,
      logo,
      isFavouriteDeal,
      userLocation,
      showDealLocation,
      capitaliseHeader,
      SectionHeader,
      sectionHeaderText,
      sectionBodyText,
      keyExtractor,
      showSectionHeader,
      linkedEntityUrl
    } = this.props
    const {load} = this.state

    return (
      <div id={this.id}>
        {showSectionHeader && (
          <SectionHeader
            id={this.id}
            tag={tag}
            logo={logo}
            userLocation={userLocation}
            capitalise={capitaliseHeader}
            headerText={sectionHeaderText}
            linkedEntityUrl={linkedEntityUrl}
            bodyText={sectionBodyText} />
        )}
        <Row>
          <Loading load={load}>
            <Slider id={this.id} className={className} settings={this.settings}>
              {data.map((item, key) => (
                <DealContainer key={keyExtractor(item)}>
                  {this.isLocked && (
                    <DealOverlay>
                      <img src={padlockIcon} alt="padlock" />
                      <BodyWithShadow color="white" weight="bold" textAlign="center">
                        <FormattedMessage id="perks.locked.overlay.message" />
                      </BodyWithShadow>
                    </DealOverlay>
                  )}
                  <DealCard
                    isFavouriteDeal={isFavouriteDeal}
                    showLocation={showDealLocation}
                    id={id(keyExtractor(item, key), this.id)}
                    deal={item}
                    page={DEAL_PAGES.HOME}
                    mode={this.mode}
                    trackingData={{
                      position: key + 1,
                      sectionHeader: sectionHeaderText,
                      userLocation
                    }} />
                </DealContainer>
              ))}
            </Slider>
          </Loading>
        </Row>
      </div>
    )
  }
}

const mapStateToProps = (state: ReduxState) => ({
  isMobile: state.mq.lessThan.medium
})

export default connect(mapStateToProps)(FeedSliderGraph)
